/**
 * PayslipsOverviewPage - Vue transverse Fiches de paie (tous salariés)
 * Affiche toutes les fiches de paie avec filtres par période, employé
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { 
  Receipt, Search, Download, Eye, EyeOff, Calendar, Users, Euro,
  Loader2, AlertCircle, FileText, Filter, ChevronRight
} from "lucide-react";
import { formatDateFr } from "../../components/ui/date-picker-fr";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

export default function PayslipsOverviewPage() {
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters - default to current year but will be updated after fetching available years
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Reveal state for net amounts
  const [revealedIds, setRevealedIds] = useState(new Set());
  
  const token = localStorage.getItem("token");

  // Fetch all employees first
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/hr/employees?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, [token]);

  // Fetch all payslips
  const fetchPayslips = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/hr/payslips/all`, {
        params: {
          year: filterYear,
          month: filterMonth !== "all" ? filterMonth : undefined,
          employee_id: filterEmployee !== "all" ? filterEmployee : undefined,
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayslips(response.data.payslips || []);
      setError(null);
    } catch (err) {
      // If endpoint doesn't exist, fetch per employee
      console.error("Error fetching payslips:", err);
      await fetchPayslipsPerEmployee();
    } finally {
      setLoading(false);
    }
  }, [token, filterYear, filterMonth, filterEmployee]);

  // Fallback: fetch payslips per employee
  const fetchPayslipsPerEmployee = async () => {
    setLoading(true);
    try {
      const allPayslips = [];
      for (const emp of employees) {
        try {
          const response = await axios.get(
            `${API}/api/hr/employees/${emp.id}/payslips`,
            {
              params: { year: filterYear },
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          const empPayslips = (response.data.payslips || []).map(p => ({
            ...p,
            employee: emp
          }));
          allPayslips.push(...empPayslips);
        } catch (e) {
          // Skip employee if error
        }
      }
      setPayslips(allPayslips);
      setError(null);
    } catch (err) {
      setError("Erreur lors du chargement des fiches de paie");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Fetch available years from all employees' payslips
  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        const response = await axios.get(`${API}/api/hr/payslips/years`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const years = (response.data.years || []).map(y => y.year).sort((a, b) => b - a);
        setAvailableYears(years);
        // Set filter to most recent year with data
        if (years.length > 0 && !years.includes(filterYear)) {
          setFilterYear(years[0]);
        }
      } catch (err) {
        console.error("Error fetching available years:", err);
        // Fallback to reasonable range
        setAvailableYears([2026, 2025, 2024, 2023]);
      }
    };
    fetchAvailableYears();
  }, [token]);

  useEffect(() => {
    if (employees.length > 0) {
      fetchPayslipsPerEmployee();
    }
  }, [employees, filterYear]);

  // Filter payslips
  const filteredPayslips = payslips.filter(p => {
    if (filterMonth !== "all" && p.month !== parseInt(filterMonth)) return false;
    if (filterEmployee !== "all" && p.employee_id !== filterEmployee) return false;
    if (searchQuery) {
      const emp = p.employee || employees.find(e => e.id === p.employee_id);
      if (!emp) return false;
      const name = `${emp.personal_info?.first_name} ${emp.personal_info?.last_name}`.toLowerCase();
      if (!name.includes(searchQuery.toLowerCase())) return false;
    }
    return true;
  });

  // Sort by date desc
  const sortedPayslips = [...filteredPayslips].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  // Toggle reveal
  const toggleReveal = (id) => {
    setRevealedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        // Auto-hide after 30s
        setTimeout(() => {
          setRevealedIds(p => {
            const s = new Set(p);
            s.delete(id);
            return s;
          });
        }, 30000);
      }
      return newSet;
    });
  };

  // Download payslip PDF
  const handleDownload = async (payslip, e) => {
    e.stopPropagation();
    
    if (!payslip.file_id) {
      toast.error("Fichier non disponible", {
        description: "Cette fiche de paie n'a pas de fichier PDF associé"
      });
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/api/hr/files/${payslip.file_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get employee name for filename
      const emp = payslip.employee || employees.find(e => e.id === payslip.employee_id);
      const empName = emp ? `${emp.personal_info?.last_name || ''}_${emp.personal_info?.first_name || ''}` : 'employe';
      const monthName = getMonthLabel(payslip.month);
      link.download = payslip.original_filename || `fiche_paie_${empName}_${monthName}_${payslip.year}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading payslip:", err);
      toast.error("Erreur de téléchargement", {
        description: "Impossible de télécharger le fichier"
      });
    }
  };

  // Calculate totals - handle string/null values
  const totalGross = sortedPayslips.reduce((sum, p) => sum + (parseFloat(p.gross_amount) || 0), 0);
  const totalNet = sortedPayslips.reduce((sum, p) => sum + (parseFloat(p.net_amount) || 0), 0);

  const getMonthLabel = (month) => MONTHS.find(m => m.value === String(month))?.label || month;

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="payslips-overview-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-blue-600" />
            Fiches de paie
          </h1>
          <p className="text-slate-500 mt-1">Vue transverse de toutes les fiches de paie</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-blue-600 font-medium">Fiches</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900">{sortedPayslips.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-green-600 font-medium">Salariés</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900">
                  {new Set(sortedPayslips.map(p => p.employee_id)).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
                <Euro className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-amber-600 font-medium">Total Brut</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-amber-900 truncate" title={`${Math.round(totalGross).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €`}>
                  {Math.round(totalGross).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
                <Euro className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-emerald-600 font-medium">Total Net</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-emerald-900 truncate" title={`${Math.round(totalNet).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €`}>
                  {Math.round(totalNet).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtres:</span>
            </div>
            
            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-4">
              <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(Number(v))}>
                <SelectTrigger className="w-full sm:w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.length > 0 ? (
                    availableYears.map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))
                  ) : (
                    [2026, 2025, 2024, 2023].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Tous les mois" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les mois</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Tous les salariés" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les salariés</SelectItem>
                {employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.personal_info?.last_name} {e.personal_info?.first_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table/Cards */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : sortedPayslips.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Receipt className="w-12 h-12 mb-4 text-slate-300" />
              <p>Aucune fiche de paie trouvée</p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="block sm:hidden divide-y divide-slate-100">
                {sortedPayslips.map(payslip => {
                  const emp = payslip.employee || employees.find(e => e.id === payslip.employee_id);
                  const isRevealed = revealedIds.has(payslip.id);
                  
                  return (
                    <div
                      key={payslip.id}
                      className="p-4 active:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/rh/salaries/${payslip.employee_id}?tab=payslips`)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm shrink-0">
                            {emp?.personal_info?.first_name?.charAt(0)}{emp?.personal_info?.last_name?.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {emp?.personal_info?.last_name} {emp?.personal_info?.first_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {getMonthLabel(payslip.month)} {payslip.year}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                        <div>
                          <p className="text-xs text-slate-500">Brut</p>
                          <p className="font-mono text-sm">{payslip.gross_amount?.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Net</p>
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-sm">
                                {isRevealed && payslip.net_amount
                                  ? `${payslip.net_amount.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €`
                                  : "••••••"
                                }
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => { e.stopPropagation(); toggleReveal(payslip.id); }}
                              >
                                {isRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </Button>
                            </div>
                          </div>
                          {payslip.file_id && (
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => handleDownload(payslip, e)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Salarié</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead className="text-right">Brut</TableHead>
                      <TableHead className="text-right">Net à payer</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPayslips.map(payslip => {
                      const emp = payslip.employee || employees.find(e => e.id === payslip.employee_id);
                      const isRevealed = revealedIds.has(payslip.id);
                      
                      return (
                        <TableRow 
                          key={payslip.id} 
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/rh/salaries/${payslip.employee_id}?tab=payslips`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
                                {emp?.personal_info?.first_name?.charAt(0)}{emp?.personal_info?.last_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {emp?.personal_info?.last_name} {emp?.personal_info?.first_name}
                                </p>
                                <p className="text-xs text-slate-500">{emp?.matricule}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span>{getMonthLabel(payslip.month)} {payslip.year}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {payslip.gross_amount?.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span className="font-mono">
                                {isRevealed && payslip.net_amount
                                  ? `${payslip.net_amount.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €`
                                  : "••••••"
                                }
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => { e.stopPropagation(); toggleReveal(payslip.id); }}
                              >
                                {isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDownload(payslip, e)}
                              disabled={!payslip.file_id}
                              data-testid={`download-payslip-${payslip.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
